// PayFast Integration Utilities

export interface PayFastFormData {
  merchant_id: string;
  merchant_key: string;
  return_url: string;
  cancel_url: string;
  notify_url: string;
  m_payment_id: string; // Our donation ID
  amount: number;
  item_name: string;
  item_description?: string;
  email_address?: string;
  custom_str1?: string; // Donation ID for tracking
  custom_str2?: string; // Donor name
  custom_int1?: number; // Internal tracking
  signature?: string;
}

export interface PayFastConfig {
  merchant_id: string;
  merchant_key: string;
  passphrase?: string;
  sandbox: boolean;
}

/**
 * Generate PayFast signature parameter string
 * The actual MD5 hash should be generated server-side for security
 */
export function generatePayFastSignature(data: PayFastFormData, passphrase?: string): string {
  // Create parameter string
  const params: string[] = [];
  
  // Add all non-empty parameters (except signature) in alphabetical order
  Object.entries(data).forEach(([key, value]) => {
    if (key !== 'signature' && value !== undefined && value !== null && value !== '') {
      // URL encode the value
      params.push(`${key}=${encodeURIComponent(value.toString())}`);
    }
  });
  
  // Sort parameters alphabetically by key
  params.sort();
  
  // Create parameter string
  let paramString = params.join('&');
  
  // Add passphrase if provided
  if (passphrase && passphrase.trim() !== '') {
    paramString += `&passphrase=${encodeURIComponent(passphrase)}`;
  }
  
  // Return parameter string - server will generate MD5 hash
  return paramString;
}

/**
 * Get PayFast form action URL based on environment
 */
export function getPayFastUrl(sandbox: boolean = true): string {
  return sandbox 
    ? 'https://sandbox.payfast.co.za/eng/process'
    : 'https://www.payfast.co.za/eng/process';
}

/**
 * Create PayFast form data from donation information
 */
export function createPayFastFormData(
  config: PayFastConfig,
  donation: {
    donationId: string;
    amount: number;
    firstName: string;
    lastName: string;
    email: string;
    cellNumber: string;
    donationType: string;
  },
  urls: {
    returnUrl: string;
    cancelUrl: string;
    notifyUrl: string;
  }
): PayFastFormData {
  const formData: PayFastFormData = {
    merchant_id: config.merchant_id,
    merchant_key: config.merchant_key,
    return_url: urls.returnUrl,
    cancel_url: urls.cancelUrl,
    notify_url: urls.notifyUrl,
    m_payment_id: donation.donationId,
    amount: donation.amount,
    item_name: `Goodbye Malaria ${donation.donationType === 'monthly' ? 'Monthly' : 'One-time'} Donation`,
    item_description: `${donation.donationType === 'monthly' ? 'Monthly recurring' : 'One-time'} donation to support malaria elimination in southern Africa`,
    email_address: donation.email,
    custom_str1: donation.donationId, // For tracking
    custom_str2: `${donation.firstName} ${donation.lastName}`, // Donor name
    custom_int1: 1 // Internal tracking
  };

  // Note: Signature will be generated server-side for security
  // formData.signature = generatePayFastSignature(formData, config.passphrase);

  return formData;
}

/**
 * Submit form to PayFast programmatically
 */
export function submitToPayFast(formData: PayFastFormData, sandbox: boolean = true): void {
  console.log('Submitting to PayFast:', formData);
  console.log('PayFast URL:', getPayFastUrl(sandbox));
  
  // Create a form element
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = getPayFastUrl(sandbox);
  form.style.display = 'none';

  // Add all form fields as hidden inputs
  Object.entries(formData).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value.toString(); // Don't URL encode here - browser handles it
      form.appendChild(input);
      console.log(`Form field: ${key} = ${value}`);
    }
  });

  // Add form to document and submit
  document.body.appendChild(form);
  
  // Log form data before submission
  console.log('Form HTML:', form.innerHTML);
  
  form.submit();
  
  // Clean up after a short delay to allow submission
  setTimeout(() => {
    if (document.body.contains(form)) {
      document.body.removeChild(form);
    }
  }, 1000);
}

/**
 * Validate PayFast webhook signature
 * This should be used on the server side to verify webhook authenticity
 */
export function validatePayFastWebhook(
  data: Record<string, any>, 
  signature: string, 
  passphrase?: string
): boolean {
  const generatedSignature = generatePayFastSignature(data as PayFastFormData, passphrase);
  return generatedSignature === signature;
}
